/**
 * Thin HTTP client over Payload REST. Knows nothing about view-models —
 * returns raw Payload docs. Mappers translate to view-models.
 */

const REQUEST_TIMEOUT_MS = 8000

export class PayloadHttpClient {
  /**
   * @param baseUrl Where to reach the Payload REST API (internal URL on the server).
   * @param locale  Content locale injected into every request. Fixed per client
   *                instance — never read from shared mutable state, because the
   *                SSR server renders many requests (and locales) concurrently.
   */
  constructor(
    private readonly baseUrl: string,
    private readonly locale: string,
  ) {}

  async getCollection<T>(
    path: string,
    params: Record<string, string | number | undefined> = {},
  ): Promise<T> {
    const url = this.buildUrl(path, params)
    // Fail fast instead of hanging React state forever on a slow/down CMS.
    let resp: Response
    try {
      resp = await fetch(url.toString(), {
        headers: { accept: 'application/json' },
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      })
    } catch (err) {
      if (err instanceof DOMException && err.name === 'TimeoutError') {
        throw new Error(`Payload request timed out after ${REQUEST_TIMEOUT_MS}ms GET ${path}`)
      }
      throw err
    }
    if (!resp.ok) throw new Error(`Payload ${resp.status} GET ${path}`)
    return (await resp.json()) as T
  }

  async getGlobal<T>(slug: string, params: Record<string, string | number> = {}): Promise<T> {
    return this.getCollection<T>(`globals/${slug}`, params)
  }

  /**
   * Single point that builds every Payload request URL. Injects this client's
   * content locale as `?locale=<code>` on EVERY request (invariant
   * — no Payload fetch ever goes out locale-less). A caller may override by
   * passing an explicit `locale` param. The CMS `fallback: true` serves the
   * default-locale text where a translation is missing.
   */
  private buildUrl(path: string, params: Record<string, string | number | undefined>): string {
    const url = new URL(`/api/${path}`, this.baseUrl)
    url.searchParams.set('locale', this.locale)
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) url.searchParams.set(k, String(v))
    }
    return url.toString()
  }
}
